import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Table, Badge, Modal, Form, Row, Col, Nav, Tab, Alert, InputGroup } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import slugify from 'slugify';
import { BlogPost, BlogSEO } from '../../../types/blog';
import CategoryTagManager from './CategoryTagManager';
import blogService from '../../../services/blogService';
import api, { apiClient } from '../../../services/api';
import './blog.css';

// Get API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8083';

const Blog = () => {
    // Helper function to construct full image URL
    const getImageUrl = (imagePath: string | undefined): string => {
        if (!imagePath) return '';
        if (imagePath.startsWith('/storage/')) {
            return `${API_BASE_URL}${imagePath}`;
        }
        if (imagePath.startsWith('http')) {
            return imagePath;
        }
        return imagePath;
    };

    const [posts, setPosts] = useState<BlogPost[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState<'all' | 'draft' | 'published' | 'archived'>('all');
    const [filterCategory, setFilterCategory] = useState<string | null>(null);
    const [filterTag, setFilterTag] = useState<string | null>(null);
    const [showCategoryManager, setShowCategoryManager] = useState(false);
    const [showTagManager, setShowTagManager] = useState(false);
    const [availableCategories, setAvailableCategories] = useState<Array<{id: number, name: string}>>([]);
    const [availableTags, setAvailableTags] = useState<Array<{id: number, name: string}>>([]);

    // Form state
    const [title, setTitle] = useState('');
    const [slug, setSlug] = useState('');
    const [content, setContent] = useState('');
    const [excerpt, setExcerpt] = useState('');
    const [featuredImage, setFeaturedImage] = useState('');
    const [featuredImagePreview, setFeaturedImagePreview] = useState('');
    const [author, setAuthor] = useState('Admin');
    const [publishDate, setPublishDate] = useState(new Date().toISOString().split('T')[0]);
    const [status, setStatus] = useState<'draft' | 'published' | 'archived'>('draft');
    const [categoriesInput, setCategoriesInput] = useState('');
    const [categories, setCategories] = useState<string[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
    const [tagsInput, setTagsInput] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
    const [commentsEnabled, setCommentsEnabled] = useState(true);

    // SEO state
    const [metaTitle, setMetaTitle] = useState('');
    const [metaDescription, setMetaDescription] = useState('');
    const [metaKeywords, setMetaKeywords] = useState('');
    const [ogTitle, setOgTitle] = useState('');
    const [ogDescription, setOgDescription] = useState('');
    const [ogImage, setOgImage] = useState('');
    const [canonicalUrl, setCanonicalUrl] = useState('');

    // Load posts from API on mount
    useEffect(() => {
        loadPosts();
        loadCategoriesAndTags();
    }, []);

    // Load posts from API
    const loadPosts = async () => {
        try {
            const response = await blogService.getAll('all', 1, 100);
            if (response.success && response.data) {
                // Convert API response to BlogPost format
                const convertedPosts: BlogPost[] = response.data.map((post: any) => ({
                    id: post.public_id || `blog-${Date.now()}`,  // ✅ USE PUBLIC_ID
                    title: post.title,
                    slug: post.slug,
                    content: post.content,
                    excerpt: post.excerpt,
                    featuredImage: post.featured_image || '',
                    images: post.featured_image ? [post.featured_image] : [],
                    author: post.author,
                    publishDate: post.publish_date ? post.publish_date.split('T')[0] : new Date().toISOString().split('T')[0],
                    lastModified: post.updated_at,
                    status: post.status,
                    categories: post.category ? [post.category.name] : [],
                    tags: post.tags?.map((t: any) => t.name) || [],
                    seo: {
                        metaTitle: post.meta_title || post.title,
                        metaDescription: post.meta_description || post.excerpt,
                        metaKeywords: post.meta_keywords?.split(',').map((k: string) => k.trim()) || [],
                        ogTitle: post.og_title || post.title,
                        ogDescription: post.og_description || post.excerpt,
                        ogImage: post.og_image || post.featured_image,
                        canonicalUrl: post.canonical_url || `/blog/${post.slug}`,
                    },
                    views: post.views || 0,
                    featured: post.featured || false,
                    commentsEnabled: post.comments_enabled ?? true,
                }));
                setPosts(convertedPosts);
            }
        } catch (error) {
            console.error('Error loading posts:', error);
            // Fallback to localStorage if API fails
            const savedPosts = localStorage.getItem('blogPosts');
            if (savedPosts) {
                setPosts(JSON.parse(savedPosts));
            }
        }
    };

    // Load categories and tags from API
    const loadCategoriesAndTags = async () => {
        try {
            // Load categories
            const categoriesResponse = await blogService.getAllCategories();
            if (categoriesResponse.success && categoriesResponse.data) {
                setAvailableCategories(categoriesResponse.data.map((c: any) => ({ id: c.id, name: c.name })));
            }

            // Load tags
            const tagsResponse = await blogService.getAllTags();
            if (tagsResponse.success && tagsResponse.data) {
                setAvailableTags(tagsResponse.data.map((t: any) => ({ id: t.id, name: t.name })));
            }
        } catch (error) {
            console.error('Error loading categories and tags:', error);
            // Fallback to localStorage if API fails
            const savedCategories = localStorage.getItem('blogCategories');
            const savedTags = localStorage.getItem('blogTags');

            if (savedCategories) {
                const cats = JSON.parse(savedCategories);
                setAvailableCategories(cats.map((c: any) => ({ id: c.id, name: c.name })));
            }

            if (savedTags) {
                const tags = JSON.parse(savedTags);
                setAvailableTags(tags.map((t: any) => ({ id: t.id, name: t.name })));
            }
        }
    };

    // Backup posts to localStorage whenever they change (as fallback)
    useEffect(() => {
        if (posts.length > 0) {
            localStorage.setItem('blogPosts', JSON.stringify(posts));
        }
    }, [posts]);

    // Auto-generate slug from title
    useEffect(() => {
        if (title && !editingPost) {
            const newSlug = slugify(title, { lower: true, strict: true });
            setSlug(newSlug);
        }
    }, [title, editingPost]);

    // Auto-fill SEO fields if empty
    useEffect(() => {
        if (title && !metaTitle) {
            setMetaTitle(title);
        }
        if (excerpt && !metaDescription) {
            setMetaDescription(excerpt);
        }
    }, [title, excerpt, metaTitle, metaDescription]);

    // Rich text editor modules
    const quillModules = useMemo(() => ({
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'font': [] }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'script': 'sub' }, { 'script': 'super' }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'align': [] }],
            ['blockquote', 'code-block'],
            ['link', 'image', 'video'],
            ['clean']
        ],
    }), []);

    const quillFormats = [
        'header', 'font', 'size',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'script',
        'list', 'bullet',
        'indent',
        'align',
        'blockquote', 'code-block',
        'link', 'image', 'video'
    ];

    const handleOpenModal = (post?: BlogPost) => {
        if (post) {
            setEditingPost(post);
            setTitle(post.title);
            setSlug(post.slug);
            setContent(post.content);
            setExcerpt(post.excerpt);
            setFeaturedImage(post.featuredImage);
            // Convert relative path to full URL for preview
            setFeaturedImagePreview(getImageUrl(post.featuredImage));
            setAuthor(post.author);
            setPublishDate(post.publishDate);
            setStatus(post.status);
            setCategories(post.categories);
            setCategoriesInput(post.categories.join(', '));
            // Set category ID from name (only first category since we use category_id, not junction table)
            const cat = availableCategories.find(c => post.categories.length > 0 && c.name === post.categories[0]);
            setSelectedCategoryId(cat ? cat.id : null);

            setTags(post.tags);
            setTagsInput(post.tags.join(', '));
            // Set tag IDs from names
            const tagIds = availableTags.filter(t => post.tags.includes(t.name)).map(t => t.id);
            setSelectedTagIds(tagIds);
            setCommentsEnabled(post.commentsEnabled);

            // SEO
            setMetaTitle(post.seo.metaTitle);
            setMetaDescription(post.seo.metaDescription);
            setMetaKeywords(post.seo.metaKeywords.join(', '));
            setOgTitle(post.seo.ogTitle || '');
            setOgDescription(post.seo.ogDescription || '');
            setOgImage(post.seo.ogImage || '');
            setCanonicalUrl(post.seo.canonicalUrl || '');
        } else {
            resetForm();
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        resetForm();
    };

    const resetForm = () => {
        setEditingPost(null);
        setTitle('');
        setSlug('');
        setContent('');
        setExcerpt('');
        setFeaturedImage('');
        setFeaturedImagePreview('');
        setAuthor('Admin');
        setPublishDate(new Date().toISOString().split('T')[0]);
        setStatus('draft');
        setCategories([]);
        setCategoriesInput('');
        setSelectedCategoryId(null);
        setTags([]);
        setTagsInput('');
        setSelectedTagIds([]);
        setCommentsEnabled(true);
        setMetaTitle('');
        setMetaDescription('');
        setMetaKeywords('');
        setOgTitle('');
        setOgDescription('');
        setOgImage('');
        setCanonicalUrl('');
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            try {
                // Show preview immediately
                const reader = new FileReader();
                reader.onloadend = () => {
                    setFeaturedImagePreview(reader.result as string);
                };
                reader.readAsDataURL(file);

                // Upload to backend
                const formData = new FormData();
                formData.append('image', file);

                const data = await api.upload('/api/admin/blog/upload-image', formData);

                if (data.success) {
                    // Store the server path (e.g., /storage/blog/20260209_150405_abc123.jpg)
                    setFeaturedImage(data.url);
                    console.log('Image uploaded successfully:', data.url);
                } else {
                    alert('Failed to upload image: ' + (data.error || 'Unknown error'));
                    setFeaturedImage('');
                    setFeaturedImagePreview('');
                }
            } catch (error) {
                console.error('Error uploading image:', error);
                alert('Failed to upload image. Please try again.');
                setFeaturedImage('');
                setFeaturedImagePreview('');
            }
        }
    };

    const handleSavePost = async () => {
        // Validate required fields
        if (!selectedCategoryId) {
            alert('Please select a category before saving');
            return;
        }

        try {
            const seo: BlogSEO = {
                metaTitle: metaTitle || title,
                metaDescription: metaDescription || excerpt,
                metaKeywords: metaKeywords.split(',').map(k => k.trim()).filter(k => k),
                ogTitle: ogTitle || title,
                ogDescription: ogDescription || excerpt,
                ogImage: ogImage || featuredImage,
                canonicalUrl: canonicalUrl || `/blog/${slug}`,
            };

            const postData: BlogPost = {
                id: editingPost?.id || `blog-${Date.now()}`,
                title,
                slug,
                content,
                excerpt,
                featuredImage,
                images: [featuredImage],
                author,
                publishDate,
                lastModified: new Date().toISOString(),
                status,
                categories: categoriesInput.split(',').map(c => c.trim()).filter(c => c),
                tags: tagsInput.split(',').map(t => t.trim()).filter(t => t),
                seo,
                views: editingPost?.views || 0,
                commentsEnabled,
            };

            // Convert to API request format
            const apiRequest = {
                ...blogService.convertToRequest(postData),
                category_id: selectedCategoryId,
                tag_ids: selectedTagIds,
            };

            // Check if this is an update (valid UUID public_id) or create (new post or localStorage post)
            const isValidPublicId = editingPost?.id && editingPost.id.includes('-') && !editingPost.id.startsWith('blog-');

            if (editingPost && isValidPublicId) {
                // Update existing post in database by public_id
                await blogService.updateByPublicId(editingPost.id, apiRequest);
                setPosts(posts.map(p => p.id === editingPost.id ? postData : p));
            } else {
                // Create new post (either no editingPost, or it's a localStorage-only post)
                const response = await blogService.create(apiRequest);
                if (response.success) {
                    // Reload posts from API to get the new post with its public_id
                    await loadPosts();
                }
            }

            // Update category and tag post counts
            updateCategoryTagCounts();

            // Also save to localStorage as backup
            localStorage.setItem('blogPosts', JSON.stringify(posts));

            handleCloseModal();
        } catch (error: any) {
            console.error('Error saving blog post:', error);
            const errorMessage = error?.message || error?.response?.data?.error || 'Unknown error occurred';
            console.log(errorMessage)
            alert(`Error saving blog post`);
        }
    };

    const updateCategoryTagCounts = () => {
        const allPosts = JSON.parse(localStorage.getItem('blogPosts') || '[]');

        // Update categories
        const savedCategories = localStorage.getItem('blogCategories');
        if (savedCategories) {
            const categories = JSON.parse(savedCategories);
            const updatedCategories = categories.map((cat: any) => {
                const count = allPosts.filter((post: BlogPost) =>
                    post.categories.includes(cat.name)
                ).length;
                return { ...cat, postCount: count };
            });
            localStorage.setItem('blogCategories', JSON.stringify(updatedCategories));
        }

        // Update tags
        const savedTags = localStorage.getItem('blogTags');
        if (savedTags) {
            const tags = JSON.parse(savedTags);
            const updatedTags = tags.map((tag: any) => {
                const count = allPosts.filter((post: BlogPost) =>
                    post.tags.includes(tag.name)
                ).length;
                return { ...tag, postCount: count };
            });
            localStorage.setItem('blogTags', JSON.stringify(updatedTags));
        }
    };

    const handleDeletePost = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this post?')) {
            try {
                // Check if it's a valid UUID public_id
                const isValidPublicId = id && id.includes('-') && !id.startsWith('blog-');
                if (isValidPublicId) {
                    await blogService.deleteByPublicId(id);
                }
                setPosts(posts.filter(p => p.id !== id));
                // Update localStorage
                localStorage.setItem('blogPosts', JSON.stringify(posts.filter(p => p.id !== id)));
            } catch (error) {
                console.error('Error deleting blog post:', error);
                alert('Error deleting blog post. Please check the console for details.');
            }
        }
    };

    const handleDuplicatePost = async (post: BlogPost) => {
        try {
            const duplicatedPost: BlogPost = {
                ...post,
                id: `blog-${Date.now()}`,
                title: `${post.title} (Copy)`,
                slug: `${post.slug}-copy-${Date.now()}`,
                status: 'draft',
                publishDate: new Date().toISOString().split('T')[0],
            };

            // Convert to API request format and create in database
            const apiRequest = blogService.convertToRequest(duplicatedPost);
            const response = await blogService.create(apiRequest);

            if (response.success) {
                // Reload posts from API to get the new duplicated post with its ID
                await loadPosts();
                alert('Post duplicated successfully!');
            } else {
                alert('Failed to duplicate post. Please try again.');
            }
        } catch (error) {
            console.error('Error duplicating post:', error);
            alert('Failed to duplicate post. Please check the console for details.');
        }
    };

    const handleToggleFeatured = async (postId: string) => {
        try {
            const isValidPublicId = postId && postId.includes('-') && !postId.startsWith('blog-');
            if (!isValidPublicId) {
                alert('Cannot toggle featured status for posts not yet saved to database');
                return;
            }

            const data = await api.post(`/api/admin/blog/posts/public/${postId}/toggle-featured`);

            if (data.success) {
                // Update the post in the local state
                setPosts(posts.map(p =>
                    p.id === postId ? { ...p, featured: data.data.featured } : p
                ));
                alert(data.data.featured ? 'Post pinned successfully!' : 'Post unpinned successfully!');
            } else {
                alert('Failed to toggle featured status');
            }
        } catch (error) {
            console.error('Error toggling featured status:', error);
            alert('Failed to toggle featured status. Please try again.');
        }
    };

    const filteredPosts = posts.filter(post => {
        const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || post.status === filterStatus;
        const matchesCategory = !filterCategory || post.categories.includes(filterCategory);
        const matchesTag = !filterTag || post.tags.includes(filterTag);
        return matchesSearch && matchesStatus && matchesCategory && matchesTag;
    });

    const statusBadgeVariant = (status: string) => {
        switch (status) {
            case 'published': return 'success';
            case 'draft': return 'warning';
            case 'archived': return 'secondary';
            default: return 'primary';
        }
    };

    // Get categories with post counts
    const categoriesWithCounts = useMemo(() => {
        const counts: { [key: string]: number } = {};
        posts.forEach(post => {
            post.categories.forEach(cat => {
                counts[cat] = (counts[cat] || 0) + 1;
            });
        });
        return availableCategories.map(cat => ({
            ...cat,
            postCount: counts[cat.name] || 0
        }));
    }, [posts, availableCategories]);

    // Get tags with post counts
    const tagsWithCounts = useMemo(() => {
        const counts: { [key: string]: number } = {};
        posts.forEach(post => {
            post.tags.forEach(tag => {
                counts[tag] = (counts[tag] || 0) + 1;
            });
        });
        return availableTags.map(tag => ({
            ...tag,
            postCount: counts[tag.name] || 0
        }));
    }, [posts, availableTags]);

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-none d-lg-block">
                    <h4 className="mb-1">Blog Management</h4>
                    <p className="text-muted mb-0">Create and manage your blog posts with full SEO optimization</p>
                </div>
                <Button variant="primary" onClick={() => handleOpenModal()}>
                    <i className="fa fa-plus me-2"></i>New Post
                </Button>
            </div>

            {/* Filters */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="mb-3">
                        <Col md={6}>
                            <InputGroup>
                                <InputGroup.Text>
                                    <i className="fa fa-search"></i>
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Search posts by title or content..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                        <Col md={6}>
                            <Form.Select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value as any)}
                            >
                                <option value="all">All Status ({posts.length})</option>
                                <option value="published">Published ({posts.filter(p => p.status === 'published').length})</option>
                                <option value="draft">Draft ({posts.filter(p => p.status === 'draft').length})</option>
                                <option value="archived">Archived ({posts.filter(p => p.status === 'archived').length})</option>
                            </Form.Select>
                        </Col>
                    </Row>

                    {/* Categories and Tags Filter */}
                    <Row>
                        <Col md={6}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <small className="text-muted fw-bold">CATEGORIES</small>
                                <Button
                                    size="sm"
                                    variant="link"
                                    className="text-decoration-none p-0"
                                    onClick={() => setShowCategoryManager(true)}
                                >
                                    <i className="fa fa-cog me-1"></i>Manage
                                </Button>
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                                <Badge
                                    bg={!filterCategory ? "primary" : "light"}
                                    text={!filterCategory ? "white" : "dark"}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setFilterCategory(null)}
                                >
                                    All ({posts.length})
                                </Badge>
                                {categoriesWithCounts.map((cat) => (
                                    <Badge
                                        key={cat.id}
                                        bg={filterCategory === cat.name ? "primary" : "light"}
                                        text={filterCategory === cat.name ? "white" : "dark"}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setFilterCategory(cat.name)}
                                    >
                                        {cat.name} ({cat.postCount})
                                    </Badge>
                                ))}
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <small className="text-muted fw-bold">TAGS</small>
                                <Button
                                    size="sm"
                                    variant="link"
                                    className="text-decoration-none p-0"
                                    onClick={() => setShowTagManager(true)}
                                >
                                    <i className="fa fa-cog me-1"></i>Manage
                                </Button>
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                                <Badge
                                    bg={!filterTag ? "secondary" : "light"}
                                    text={!filterTag ? "white" : "dark"}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setFilterTag(null)}
                                >
                                    All ({posts.length})
                                </Badge>
                                {tagsWithCounts.map((tag) => (
                                    <Badge
                                        key={tag.id}
                                        bg={filterTag === tag.name ? "secondary" : "light"}
                                        text={filterTag === tag.name ? "white" : "dark"}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setFilterTag(tag.name)}
                                    >
                                        {tag.name} ({tag.postCount})
                                    </Badge>
                                ))}
                            </div>
                        </Col>
                    </Row>

                    {/* Active Filters Display */}
                    {(filterCategory || filterTag) && (
                        <Row className="mt-3">
                            <Col>
                                <div className="d-flex align-items-center gap-2">
                                    <small className="text-muted">Active filters:</small>
                                    {filterCategory && (
                                        <Badge bg="primary" className="d-flex align-items-center gap-1">
                                            Category: {filterCategory}
                                            <i
                                                className="fa fa-times"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => setFilterCategory(null)}
                                            ></i>
                                        </Badge>
                                    )}
                                    {filterTag && (
                                        <Badge bg="secondary" className="d-flex align-items-center gap-1">
                                            Tag: {filterTag}
                                            <i
                                                className="fa fa-times"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => setFilterTag(null)}
                                            ></i>
                                        </Badge>
                                    )}
                                    <Button
                                        size="sm"
                                        variant="link"
                                        className="text-decoration-none p-0"
                                        onClick={() => {
                                            setFilterCategory(null);
                                            setFilterTag(null);
                                        }}
                                    >
                                        Clear all
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    )}
                </Card.Body>
            </Card>

            {/* Posts Table */}
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    {filteredPosts.length === 0 ? (
                        <div className="text-center py-5">
                            <div style={{ fontSize: '4rem' }} className="mb-3">📝</div>
                            <p className="fs-5 text-muted">No blog posts yet</p>
                            <p className="small text-muted">Create your first post to get started</p>
                            <Button variant="primary" onClick={() => handleOpenModal()}>
                                <i className="fa fa-plus me-2"></i>Create First Post
                            </Button>
                        </div>
                    ) : (
                        <Table hover responsive className="mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th style={{ width: '80px' }}>Image</th>
                                    <th>Title</th>
                                    <th>Author</th>
                                    <th>Status</th>
                                    <th>Category</th>
                                    <th>Tags</th>
                                    <th>Date</th>
                                    <th style={{ width: '200px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPosts.map((post) => (
                                    <tr key={post.id}>
                                        <td>
                                            {post.featuredImage ? (
                                                <img
                                                    src={getImageUrl(post.featuredImage)}
                                                    alt={post.title}
                                                    style={{
                                                        width: '60px',
                                                        height: '60px',
                                                        objectFit: 'cover',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    style={{
                                                        width: '60px',
                                                        height: '60px',
                                                        backgroundColor: '#f0f0f0',
                                                        borderRadius: '8px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    <i className="fa fa-image text-muted"></i>
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div>
                                                <div className="fw-bold">{post.title}</div>
                                                <small className="text-muted">/{post.slug}</small>
                                            </div>
                                        </td>
                                        <td>{post.author}</td>
                                        <td>
                                            <Badge bg={statusBadgeVariant(post.status)}>
                                                {post.status}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1">
                                                {post.categories.length > 0 ? post.categories.map((cat, idx) => (
                                                    <Badge
                                                        key={idx}
                                                        bg="primary"
                                                        className="small"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => setFilterCategory(cat)}
                                                    >
                                                        {cat}
                                                    </Badge>
                                                )) : (
                                                    <small className="text-muted">—</small>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1">
                                                {post.tags.length > 0 ? post.tags.slice(0, 3).map((tag, idx) => (
                                                    <Badge
                                                        key={idx}
                                                        bg="secondary"
                                                        className="small"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => setFilterTag(tag)}
                                                    >
                                                        {tag}
                                                    </Badge>
                                                )) : (
                                                    <small className="text-muted">—</small>
                                                )}
                                                {post.tags.length > 3 && (
                                                    <Badge bg="secondary" className="small">
                                                        +{post.tags.length - 3}
                                                    </Badge>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <small>{new Date(post.publishDate).toLocaleDateString()}</small>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    size="sm"
                                                    variant={post.featured ? "warning" : "outline-warning"}
                                                    onClick={() => handleToggleFeatured(post.id)}
                                                    title={post.featured ? "Unpin from homepage" : "Pin to homepage"}
                                                >
                                                    <i className={post.featured ? "fa fa-star" : "fa fa-star-o"}></i>
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    onClick={() => handleOpenModal(post)}
                                                    title="Edit"
                                                >
                                                    <i className="fa fa-edit"></i>
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    onClick={() => handleDuplicatePost(post)}
                                                    title="Duplicate"
                                                >
                                                    <i className="fa fa-copy"></i>
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline-danger"
                                                    onClick={() => handleDeletePost(post.id)}
                                                    title="Delete"
                                                >
                                                    <i className="fa fa-trash"></i>
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {/* Blog Post Modal */}
            <Modal
                show={showModal}
                onHide={handleCloseModal}
                size="xl"
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingPost ? 'Edit Blog Post' : 'Create New Blog Post'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                    <Tab.Container defaultActiveKey="content">
                        <Nav variant="tabs" className="mb-4">
                            <Nav.Item>
                                <Nav.Link eventKey="content">
                                    <i className="fa fa-file-text me-2"></i>Content
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="media">
                                    <i className="fa fa-image me-2"></i>Media
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="categories">
                                    <i className="fa fa-tags me-2"></i>Categories & Tags
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="seo">
                                    <i className="fa fa-search me-2"></i>SEO
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="settings">
                                    <i className="fa fa-cog me-2"></i>Settings
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>

                        <Tab.Content>
                            {/* Content Tab */}
                            <Tab.Pane eventKey="content">
                                <Form.Group className="mb-3">
                                    <Form.Label>Title <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter your blog post title..."
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>URL Slug <span className="text-danger">*</span></Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>/blog/</InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            placeholder="url-slug"
                                            value={slug}
                                            onChange={(e) => setSlug(e.target.value)}
                                            required
                                        />
                                    </InputGroup>
                                    <Form.Text className="text-muted">
                                        Auto-generated from title. You can customize it.
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Excerpt / Summary <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder="Brief summary of your blog post (shown in blog list)"
                                        value={excerpt}
                                        onChange={(e) => setExcerpt(e.target.value)}
                                        required
                                    />
                                    <Form.Text className="text-muted">
                                        {excerpt.length}/160 characters (recommended for SEO)
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Content <span className="text-danger">*</span></Form.Label>
                                    <div className="blog-editor">
                                        <ReactQuill
                                            theme="snow"
                                            value={content}
                                            onChange={setContent}
                                            modules={quillModules}
                                            formats={quillFormats}
                                            placeholder="Write your amazing blog post here..."
                                            style={{ height: '400px', marginBottom: '60px' }}
                                        />
                                    </div>
                                </Form.Group>
                            </Tab.Pane>

                            {/* Media Tab */}
                            <Tab.Pane eventKey="media">
                                <Alert variant="info">
                                    <i className="fa fa-info-circle me-2"></i>
                                    Upload a featured image for your blog post. This image will be shown in blog lists and social media shares.
                                </Alert>

                                <Form.Group className="mb-3">
                                    <Form.Label>Featured Image</Form.Label>
                                    <Form.Control
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                    />
                                    <Form.Text className="text-muted">
                                        Recommended size: 1200x630px for best social media sharing
                                    </Form.Text>
                                </Form.Group>

                                {featuredImagePreview && (
                                    <div className="mt-3">
                                        <Form.Label>Image Preview</Form.Label>
                                        <div className="position-relative">
                                            <img
                                                src={featuredImagePreview}
                                                alt="Featured preview"
                                                style={{
                                                    width: '100%',
                                                    maxWidth: '600px',
                                                    height: 'auto',
                                                    borderRadius: '8px',
                                                    border: '1px solid #dee2e6'
                                                }}
                                            />
                                            <Button
                                                size="sm"
                                                variant="danger"
                                                className="position-absolute top-0 end-0 m-2"
                                                onClick={() => {
                                                    setFeaturedImage('');
                                                    setFeaturedImagePreview('');
                                                }}
                                            >
                                                <i className="fa fa-times"></i> Remove
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                <Form.Group className="mt-4">
                                    <Form.Label>Or use Image URL</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="https://example.com/image.jpg"
                                        value={featuredImage}
                                        onChange={(e) => {
                                            setFeaturedImage(e.target.value);
                                            setFeaturedImagePreview(e.target.value);
                                        }}
                                    />
                                </Form.Group>
                            </Tab.Pane>

                            {/* Categories & Tags Tab */}
                            <Tab.Pane eventKey="categories">
                                <Alert variant="info">
                                    <i className="fa fa-info-circle me-2"></i>
                                    Select from existing categories and tags, or type custom ones. Manage your categories and tags using the buttons below.
                                </Alert>

                                <Form.Group className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Form.Label className="mb-0">Categories <span className="text-danger">*</span></Form.Label>
                                        <Button
                                            size="sm"
                                            variant="outline-primary"
                                            onClick={() => {
                                                setShowCategoryManager(true);
                                            }}
                                        >
                                            <i className="fa fa-cog me-1"></i>Manage Categories
                                        </Button>
                                    </div>

                                    {availableCategories.length > 0 && (
                                        <div className="border rounded p-3 mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                            <small className="text-muted d-block mb-2">Select from existing:</small>
                                            <Row>
                                                {availableCategories.map((cat, idx) => {
                                                    const isChecked = selectedCategoryId === cat.id;

                                                    return (
                                                        <Col md={6} key={idx} className="mb-2">
                                                            <Form.Check
                                                                type="radio"
                                                                id={`cat-${idx}`}
                                                                name="category"
                                                                label={cat.name}
                                                                checked={isChecked}
                                                                onChange={() => {
                                                                    setSelectedCategoryId(cat.id);
                                                                }}
                                                            />
                                                        </Col>
                                                    );
                                                })}
                                            </Row>
                                        </div>
                                    )}

                                    <Form.Text className="text-muted">
                                        <strong>Required:</strong> Select a category from the options above. Use "Manage Categories" to add new ones.
                                    </Form.Text>
                                    {selectedCategoryId && (
                                        <div className="mt-2">
                                            <strong>Selected: </strong>
                                            {(() => {
                                                const category = availableCategories.find(c => c.id === selectedCategoryId);
                                                return category ? (
                                                    <Badge bg="primary" className="me-1">
                                                        {category.name}
                                                        <i
                                                            className="fa fa-times ms-1"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => {
                                                                setSelectedCategoryId(null);
                                                            }}
                                                        ></i>
                                                    </Badge>
                                                ) : null;
                                            })()}
                                        </div>
                                    )}
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Form.Label className="mb-0">Tags</Form.Label>
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            onClick={() => {
                                                setShowTagManager(true);
                                            }}
                                        >
                                            <i className="fa fa-cog me-1"></i>Manage Tags
                                        </Button>
                                    </div>

                                    {availableTags.length > 0 && (
                                        <div className="border rounded p-3 mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                            <small className="text-muted d-block mb-2">Select from existing:</small>
                                            <Row>
                                                {availableTags.map((tag, idx) => {
                                                    const isChecked = selectedTagIds.includes(tag.id);

                                                    return (
                                                        <Col md={4} key={idx} className="mb-2">
                                                            <Form.Check
                                                                type="checkbox"
                                                                id={`tag-${idx}`}
                                                                label={tag.name}
                                                                checked={isChecked}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setSelectedTagIds([...selectedTagIds, tag.id]);
                                                                    } else {
                                                                        setSelectedTagIds(selectedTagIds.filter(id => id !== tag.id));
                                                                    }
                                                                }}
                                                            />
                                                        </Col>
                                                    );
                                                })}
                                            </Row>
                                        </div>
                                    )}

                                    <Form.Text className="text-muted">
                                        Select tags from the checkboxes above. Use "Manage Tags" to add new ones.
                                    </Form.Text>
                                    {selectedTagIds.length > 0 && (
                                        <div className="mt-2">
                                            <strong>Selected: </strong>
                                            {selectedTagIds.map((tagId) => {
                                                const tag = availableTags.find(t => t.id === tagId);
                                                return tag ? (
                                                    <Badge key={tagId} bg="secondary" className="me-1">
                                                        {tag.name}
                                                        <i
                                                            className="fa fa-times ms-1"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => {
                                                                setSelectedTagIds(selectedTagIds.filter(id => id !== tagId));
                                                            }}
                                                        ></i>
                                                    </Badge>
                                                ) : null;
                                            })}
                                        </div>
                                    )}
                                </Form.Group>

                                <Alert variant="success" className="mt-4">
                                    <strong>SEO Tip:</strong> Use 2-4 broad categories and 5-10 specific tags for optimal organization.
                                </Alert>
                            </Tab.Pane>

                            {/* SEO Tab */}
                            <Tab.Pane eventKey="seo">
                                <Alert variant="info">
                                    <i className="fa fa-info-circle me-2"></i>
                                    Optimize your blog post for search engines. These fields are auto-filled but you can customize them.
                                </Alert>

                                <h5 className="mb-3">Meta Tags</h5>

                                <Form.Group className="mb-3">
                                    <Form.Label>Meta Title</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="SEO optimized title"
                                        value={metaTitle}
                                        onChange={(e) => setMetaTitle(e.target.value)}
                                    />
                                    <Form.Text className="text-muted">
                                        {metaTitle.length}/60 characters (Google displays ~60 chars)
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Meta Description</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        placeholder="SEO optimized description"
                                        value={metaDescription}
                                        onChange={(e) => setMetaDescription(e.target.value)}
                                    />
                                    <Form.Text className="text-muted">
                                        {metaDescription.length}/160 characters (Google displays ~160 chars)
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <Form.Label>Meta Keywords</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="keyword1, keyword2, keyword3"
                                        value={metaKeywords}
                                        onChange={(e) => setMetaKeywords(e.target.value)}
                                    />
                                    <Form.Text className="text-muted">
                                        Comma-separated keywords for SEO
                                    </Form.Text>
                                </Form.Group>

                                <hr />

                                <h5 className="mb-3 mt-4">Open Graph (Social Media)</h5>

                                <Form.Group className="mb-3">
                                    <Form.Label>OG Title</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Title for social media shares"
                                        value={ogTitle}
                                        onChange={(e) => setOgTitle(e.target.value)}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>OG Description</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        placeholder="Description for social media shares"
                                        value={ogDescription}
                                        onChange={(e) => setOgDescription(e.target.value)}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <Form.Label>OG Image URL</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Image URL for social media"
                                        value={ogImage}
                                        onChange={(e) => setOgImage(e.target.value)}
                                    />
                                    <Form.Text className="text-muted">
                                        Defaults to featured image if not specified
                                    </Form.Text>
                                </Form.Group>

                                <hr />

                                <h5 className="mb-3 mt-4">Advanced SEO</h5>

                                <Form.Group className="mb-3">
                                    <Form.Label>Canonical URL</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="https://pure-key-homes.es/blog/your-post"
                                        value={canonicalUrl}
                                        onChange={(e) => setCanonicalUrl(e.target.value)}
                                    />
                                    <Form.Text className="text-muted">
                                        Prevents duplicate content issues. Auto-generated if not specified.
                                    </Form.Text>
                                </Form.Group>

                                <Alert variant="success" className="mt-4">
                                    <strong>SEO Score:</strong> {(() => {
                                        let score = 0;
                                        if (metaTitle.length >= 30 && metaTitle.length <= 60) score += 25;
                                        if (metaDescription.length >= 120 && metaDescription.length <= 160) score += 25;
                                        if (metaKeywords.split(',').length >= 3) score += 25;
                                        if (featuredImage) score += 25;
                                        return score;
                                    })()}%
                                    {(() => {
                                        const score = (() => {
                                            let s = 0;
                                            if (metaTitle.length >= 30 && metaTitle.length <= 60) s += 25;
                                            if (metaDescription.length >= 120 && metaDescription.length <= 160) s += 25;
                                            if (metaKeywords.split(',').length >= 3) s += 25;
                                            if (featuredImage) s += 25;
                                            return s;
                                        })();

                                        if (score === 100) return ' - Excellent! 🎉';
                                        if (score >= 75) return ' - Good! 👍';
                                        if (score >= 50) return ' - Fair ⚠️';
                                        return ' - Needs improvement ❌';
                                    })()}
                                </Alert>
                            </Tab.Pane>

                            {/* Settings Tab */}
                            <Tab.Pane eventKey="settings">
                                <Form.Group className="mb-3">
                                    <Form.Label>Author</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Author name"
                                        value={author}
                                        onChange={(e) => setAuthor(e.target.value)}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Publish Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={publishDate}
                                        onChange={(e) => setPublishDate(e.target.value)}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value as any)}
                                    >
                                        <option value="draft">Draft (not visible to public)</option>
                                        <option value="published">Published (visible to everyone)</option>
                                        <option value="archived">Archived (hidden but saved)</option>
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="comments-switch"
                                        label="Enable comments"
                                        checked={commentsEnabled}
                                        onChange={(e) => setCommentsEnabled(e.target.checked)}
                                    />
                                    <Form.Text className="text-muted">
                                        Allow readers to leave comments on this post
                                    </Form.Text>
                                </Form.Group>

                                {editingPost && (
                                    <Alert variant="secondary">
                                        <strong>Post Statistics:</strong><br />
                                        Created: {new Date(editingPost.publishDate).toLocaleString()}<br />
                                        {editingPost.lastModified && (
                                            <>Last Modified: {new Date(editingPost.lastModified).toLocaleString()}<br /></>
                                        )}
                                        Views: {editingPost.views || 0}
                                    </Alert>
                                )}
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSavePost}
                        disabled={!title || !slug || !content || !excerpt || !selectedCategoryId}
                    >
                        <i className="fa fa-save me-2"></i>
                        {editingPost ? 'Update Post' : 'Create Post'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Category & Tag Managers */}
            <CategoryTagManager
                show={showCategoryManager}
                onHide={() => {
                    setShowCategoryManager(false);
                    loadCategoriesAndTags();
                }}
                type="category"
            />
            <CategoryTagManager
                show={showTagManager}
                onHide={() => {
                    setShowTagManager(false);
                    loadCategoriesAndTags();
                }}
                type="tag"
            />
        </div>
    );
};

export default Blog;
